import { useEffect, useMemo, useRef, useState } from "react";
import { useOrder, meshById, MESH_OPTIONS } from "../../context/OrderContext";
import { nextBulkTier } from "../../lib/pricingConfig";
import api from "../../lib/api";
import MeasureLaterNote from "../MeasureLaterNote";
import { toast } from "sonner";
import { Minus, Plus, Sparkles, TrendingUp, Tag, X as XIcon, DoorOpen, AppWindow } from "lucide-react";
import { PromoProgressCard } from "../PromoBanner";

const MIN_QTY = 1;
const MAX_QTY = 50;

export default function StepSummary() {
  const { state, update, goNext, goBack } = useOrder();
  const [quote, setQuote] = useState(null);
  const [meshPickerOpen, setMeshPickerOpen] = useState(false);
  const [codeInput, setCodeInput] = useState(state.discountCode || "");
  const [applyingCode, setApplyingCode] = useState(false);
  const debounceRef = useRef(null);

  const isWindow = state.product?.type === "window";
  const petCount = !isWindow && state.petscreenAll ? state.quantity : 0;
  const meshType = isWindow ? (state.meshType || "standard") : "standard";
  const mesh = meshById(meshType);
  const whiteGloveEligible = isWindow && state.quantity >= 3;

  // Debounced live quote. Customer can rapid-tap qty +/- without spamming the backend.
  useEffect(() => {
    if (!state.product) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post("/quote", {
        product_slug: state.product.slug,
        quantity: state.quantity,
        petscreen_count: petCount,
        mesh_type: meshType,
        upsells: { white_glove: !!state.whiteGlove },
        state: state.customer?.state || null,
        discount_code: state.discountCode || null,
      }).then((r) => setQuote(r.data)).catch(() => {});
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [state.product, state.quantity, petCount, meshType, state.whiteGlove, state.customer?.state, state.discountCode]);

  const applyDiscount = async () => {
    const code = (codeInput || "").trim().toUpperCase();
    if (!code) return;
    setApplyingCode(true);
    try {
      const r = await api.post("/discount/validate", {
        code,
        product_slug: state.product?.slug,
        quantity: state.quantity,
        mesh_type: meshType,
        petscreen_count: petCount,
        state: state.customer?.state || null,
      });
      if (!r.data.valid) {
        toast.error(r.data.message || "Code not valid");
        return;
      }
      update({
        discountCode: code,
        discountInfo: { percent: r.data.percent, description: r.data.description || "" },
      });
      setCodeInput(code);
      toast.success(`${code} applied — ${r.data.percent}% off`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply code");
    } finally {
      setApplyingCode(false);
    }
  };

  const clearDiscount = () => {
    update({ discountCode: "", discountInfo: null });
    setCodeInput("");
  };

  // ---- Mutators ----
  const setQty = (next) => {
    const n = Math.max(MIN_QTY, Math.min(MAX_QTY, next));
    if (n === state.quantity) return;
    const patch = { quantity: n };
    // White Glove is only valid for qty>=3. Silently drop if we fall below.
    if (state.whiteGlove && n < 3) {
      patch.whiteGlove = false;
      toast.message("Perfect Fit removed", { description: "Requires 3 or more screens. You can add it back after you bump the quantity." });
    }
    // Items are populated post-checkout; if somehow present, trim/pad so length == qty.
    if (Array.isArray(state.items) && state.items.length && state.items.length !== n) {
      patch.items = [];
    }
    update(patch);
  };

  const removeMesh = () => {
    update({ meshType: "standard" });
    setMeshPickerOpen(false);
  };
  const pickMesh = (id) => {
    update({ meshType: id });
    setMeshPickerOpen(false);
  };
  const removeWhiteGlove = () => update({ whiteGlove: false });
  const addWhiteGlove = () => {
    if (!whiteGloveEligible) return;
    update({ whiteGlove: true });
  };
  const togglePetscreen = () => update({ petscreenAll: !state.petscreenAll });

  const whiteGloveFee = useMemo(() => {
    if (!whiteGloveEligible) return 0;
    if (state.quantity >= 11) return 99;
    if (state.quantity >= 6) return 79;
    return 49;
  }, [whiteGloveEligible, state.quantity]);

  // Next bulk-discount tier (window only). Used to drive the "add N more, save $X" nudge.
  const nextTier = useMemo(
    () => (isWindow ? nextBulkTier(state.quantity) : null),
    [isWindow, state.quantity],
  );

  return (
    <div data-testid="step-summary">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
        Order summary
      </h1>
      <p className="muted-text mb-5">Review and tweak before checkout.</p>
      <MeasureLaterNote />

      {/* --- Product row with inline qty stepper --- */}
      <div className="option-card mb-3" data-testid="summary-product-card">
        <div className="flex justify-between items-center gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-[var(--brand-text)] truncate">{state.product?.name}</p>
            <p className="text-sm muted-text">${state.product?.base_price.toFixed(2)} each</p>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-testid="summary-qty-stepper">
            <button
              type="button"
              data-testid="summary-qty-dec"
              onClick={() => setQty(state.quantity - 1)}
              disabled={state.quantity <= MIN_QTY}
              aria-label="Decrease quantity"
              className="w-9 h-9 rounded-full border-2 border-[var(--brand-border)] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--brand-green)]"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span
              data-testid="summary-qty-value"
              className="font-bold text-[var(--brand-text)] min-w-[2ch] text-center tabular-nums"
            >
              {state.quantity}
            </span>
            <button
              type="button"
              data-testid="summary-qty-inc"
              onClick={() => setQty(state.quantity + 1)}
              disabled={state.quantity >= MAX_QTY}
              aria-label="Increase quantity"
              className="w-9 h-9 rounded-full border-2 border-[var(--brand-border)] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-[var(--brand-green)]"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Bulk-tier nudge — only on windows, only when a higher tier is reachable. */}
        {isWindow && nextTier && (
          <button
            type="button"
            data-testid="summary-bulk-nudge"
            onClick={() => setQty(state.quantity + nextTier.screensAway)}
            className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-[var(--brand-green-light)] hover:border-[var(--brand-green)] hover:bg-[var(--brand-green-light)]/30 transition-colors text-left"
          >
            <span className="flex items-center gap-2 min-w-0">
              <TrendingUp className="w-4 h-4 brand-text shrink-0" />
              <span className="text-sm">
                <span className="font-semibold brand-text">
                  Add {nextTier.screensAway} more
                </span>
                <span className="muted-text"> · save ${nextTier.dollarOff.toFixed(0)}</span>
              </span>
            </span>
            <span className="text-xs font-semibold brand-text shrink-0">Tap to add →</span>
          </button>
        )}
      </div>

      {/* --- Sliding door PetScreen toggle --- */}
      {!isWindow && (
        <div className="option-card mb-3" data-testid="summary-petscreen-card">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-semibold text-[var(--brand-text)]">
                PetScreen upgrade {state.petscreenAll ? `(all ${state.quantity})` : ""}
              </p>
              <p className="text-sm muted-text">
                {state.petscreenAll ? `${state.quantity} × $25` : "Tough, chew-resistant mesh"}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold">
                {state.petscreenAll ? `+$${(25 * state.quantity).toFixed(2)}` : "—"}
              </p>
              <button
                type="button"
                data-testid="summary-toggle-petscreen"
                onClick={togglePetscreen}
                className="text-xs font-semibold brand-text underline underline-offset-2 mt-1"
              >
                {state.petscreenAll ? "Remove" : `Add (+$${(25 * state.quantity).toFixed(2)})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Window mesh toggle + inline picker --- */}
      {isWindow && (
        <div className="option-card mb-3" data-testid="summary-mesh-card">
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[var(--brand-text)]">
                {mesh.name}{mesh.upcharge === 0 ? " (Included)" : ""}
              </p>
              <p className="text-sm muted-text">
                {mesh.upcharge > 0
                  ? `${state.quantity} × $${mesh.upcharge}`
                  : "Durable, high-quality fiberglass mesh"}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-semibold">
                {mesh.upcharge > 0 ? `+$${(mesh.upcharge * state.quantity).toFixed(2)}` : "$0"}
              </p>
              {mesh.upcharge > 0 ? (
                <button
                  type="button"
                  data-testid="summary-remove-mesh"
                  onClick={removeMesh}
                  className="text-xs font-semibold brand-text underline underline-offset-2 mt-1"
                >
                  Remove upgrade
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="summary-add-mesh"
                  onClick={() => setMeshPickerOpen((v) => !v)}
                  className="text-xs font-semibold brand-text underline underline-offset-2 mt-1"
                >
                  {meshPickerOpen ? "Cancel" : "Add mesh upgrade →"}
                </button>
              )}
            </div>
          </div>

          {/* Inline picker, only when user clicks "Add mesh upgrade" */}
          {meshPickerOpen && mesh.upcharge === 0 && (
            <div className="mt-3 pt-3 border-t border-[var(--brand-border)] space-y-2" data-testid="summary-mesh-picker">
              {MESH_OPTIONS.filter((m) => m.id !== "standard").map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`summary-pick-mesh-${m.id}`}
                  onClick={() => pickMesh(m.id)}
                  className="w-full flex justify-between items-center text-left px-3 py-2 rounded-xl border-2 border-[var(--brand-border)] hover:border-[var(--brand-green)]"
                >
                  <span>
                    <span className="font-semibold text-[var(--brand-text)]">{m.name}</span>
                    <span className="block text-xs muted-text">{m.blurb}</span>
                  </span>
                  <span className="font-semibold brand-text shrink-0 ml-2">
                    +${(m.upcharge * state.quantity).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- White glove toggle --- */}
      {whiteGloveEligible && (
        <div className="option-card mb-3" data-testid="summary-wg-card">
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[var(--brand-text)]">Measurement &amp; Perfect Fit Guarantee</p>
              <p className="text-sm muted-text">We measure, build, and guarantee a perfect fit.</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-semibold">
                {state.whiteGlove ? `+$${whiteGloveFee.toFixed(2)}` : "—"}
              </p>
              {state.whiteGlove ? (
                <button
                  type="button"
                  data-testid="summary-remove-wg"
                  onClick={removeWhiteGlove}
                  className="text-xs font-semibold brand-text underline underline-offset-2 mt-1"
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="summary-add-wg"
                  onClick={addWhiteGlove}
                  className="text-xs font-semibold brand-text underline underline-offset-2 mt-1"
                >
                  Add it back (+${whiteGloveFee})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Savings badge: lights up whenever the customer is getting a discount --- */}
      {quote?.bulk_discount > 0 && (
        <div
          data-testid="summary-savings-badge"
          className="rounded-2xl px-4 py-3 mb-3 flex items-center gap-2 border-2"
          style={{ background: "#ecfdf5", borderColor: "#6ee7b7" }}
        >
          <Sparkles className="w-4 h-4 brand-text" />
          <p className="text-sm font-semibold brand-text">
            You&apos;re saving ${quote.bulk_discount.toFixed(2)} on bulk pricing!
          </p>
        </div>
      )}

      {/* --- Active promotion progress / unlocked banner --- */}
      <PromoProgressCard quote={quote} />

      {/* --- Promo code --- */}
      <div className="option-card mb-3" data-testid="summary-discount-card">
        {state.discountCode && quote?.discount_amount > 0 ? (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 brand-text" />
                <p className="font-semibold text-[var(--brand-text)] font-mono">{state.discountCode}</p>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: "#dcfce7", color: "#166534" }}>
                  {quote.discount_percent}% off
                </span>
              </div>
              <p className="text-xs muted-text mt-0.5">
                Saving ${quote.discount_amount.toFixed(2)} on products
              </p>
            </div>
            <button
              type="button"
              data-testid="remove-discount-btn"
              onClick={clearDiscount}
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-[var(--brand-border)] hover:border-red-300 hover:text-red-600"
              title="Remove code"
              aria-label="Remove discount code"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 muted-text" />
              <p className="font-semibold text-[var(--brand-text)] text-sm">Promo code</p>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); applyDiscount(); }}
              className="flex gap-2">
              <input
                data-testid="discount-code-input"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="Enter code"
                maxLength={40}
                className="flex-1 rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm uppercase font-mono"
              />
              <button
                type="submit"
                data-testid="apply-discount-btn"
                disabled={applyingCode || !codeInput.trim()}
                className="px-4 py-2 rounded-full text-sm font-semibold brand-bg text-white disabled:opacity-50"
              >
                {applyingCode ? "…" : "Apply"}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* --- Itemized totals --- */}
      {quote && (
        <div
          data-testid="summary-totals-card"
          className="rounded-3xl overflow-hidden border-2 border-[var(--brand-green)] bg-white shadow-sm"
        >
          {/* Product header band — image + name + tier pill + qty chip */}
          <div
            data-testid="summary-totals-product-header"
            className="flex items-center gap-3 p-3 sm:p-4 bg-[var(--brand-green-light)]/40 border-b border-[var(--brand-border)]"
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden border-2 border-white bg-white flex items-center justify-center shrink-0 shadow-sm">
              {state.product?.image_url ? (
                <img
                  src={state.product.image_url}
                  alt={state.product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : isWindow ? (
                <AppWindow className="w-7 h-7 brand-text" />
              ) : (
                <DoorOpen className="w-7 h-7 brand-text" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-[var(--brand-text)] truncate text-base sm:text-lg leading-tight">
                {state.product?.name}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {state.product?.tagline && (
                  <span className="inline-block text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-green)] text-white">
                    {state.product.tagline}
                  </span>
                )}
                <span className="text-xs muted-text">
                  ${state.product?.base_price.toFixed(0)} each
                </span>
              </div>
            </div>
            <div className="shrink-0 text-center">
              <div className="px-3 py-1.5 rounded-full bg-white border-2 border-[var(--brand-border)] font-extrabold text-[var(--brand-text)] text-sm tabular-nums">
                ×{quote.quantity}
              </div>
            </div>
          </div>

          {/* Itemized lines */}
          <div className="p-4 sm:p-5">
            <div className="flex justify-between text-sm mb-1">
              <span className="muted-text">Items ({quote.quantity}×${quote.base_price.toFixed(2)})</span>
              <span data-testid="summary-items-subtotal">${(quote.quantity * quote.base_price).toFixed(2)}</span>
            </div>
            {quote.petscreen_total > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="muted-text">PetScreen ({quote.petscreen_count}×$25)</span>
                <span>+${quote.petscreen_total.toFixed(2)}</span>
              </div>
            )}
            {quote.mesh_total > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="muted-text">{mesh.name} ({quote.quantity}×${quote.mesh_upcharge_per_item})</span>
                <span data-testid="summary-mesh-fee">+${quote.mesh_total.toFixed(2)}</span>
              </div>
            )}
            {quote.bulk_discount > 0 && (
              <div className="flex justify-between text-sm mb-1 brand-text font-semibold" data-testid="summary-bulk">
                <span>Bulk Savings</span>
                <span>−${quote.bulk_discount.toFixed(2)}</span>
              </div>
            )}
            {quote.discount_amount > 0 && (
              <div className="flex justify-between text-sm mb-1 brand-text font-semibold" data-testid="summary-discount">
                <span>Promo code ({quote.discount_code} · {quote.discount_percent}%)</span>
                <span>−${quote.discount_amount.toFixed(2)}</span>
              </div>
            )}
            {quote.promotion?.savings > 0 && (
              <div className="flex justify-between text-sm mb-1 brand-text font-semibold" data-testid="summary-promo-savings">
                <span>{quote.promotion.name || "Promo"} applied</span>
                <span>−${quote.promotion.savings.toFixed(2)}</span>
              </div>
            )}
            {quote.white_glove_fee > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="muted-text">Perfect Fit Guarantee</span>
                <span data-testid="summary-wg-fee">+${quote.white_glove_fee.toFixed(2)}</span>
              </div>
            )}
            {quote.tax > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="muted-text">Sales tax ({(quote.tax_rate * 100).toFixed(2)}% · {quote.tax_state})</span>
                <span data-testid="summary-tax">+${quote.tax.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Total row — celebratory green strip */}
          <div className="px-4 sm:px-5 py-3 sm:py-4 bg-[var(--brand-green)] text-white flex items-center justify-between">
            <span className="text-base font-extrabold tracking-tight inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Total
            </span>
            <span data-testid="summary-total" className="text-2xl font-black tabular-nums">
              ${quote.total.toFixed(2)}
            </span>
          </div>
          <p className="text-xs muted-text px-4 sm:px-5 py-2 bg-white">
            Add-ons (delivery, install) appear after checkout.
          </p>
        </div>
      )}

      <div className="flex gap-3 mt-8">
        <button data-testid="back-btn" type="button" onClick={goBack}
          className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold">
          Back
        </button>
        <button data-testid="continue-btn" className="cta-btn flex-1" onClick={goNext}>
          Proceed to checkout
        </button>
      </div>
    </div>
  );
}
